import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import {
  QUERY_KEYS,
  useConfiguredIssueStatus,
  useConfiguredIssueStatusMutation,
  useIssueStatuses,
} from '../api';
import { Field, Form, Formik } from 'formik';

import { useEffect, useState } from 'react';
import { useToastHook } from '../../appToast';
import { useQueryClient } from '@tanstack/react-query';
import Loader from '../../appLoader';
import { SaveAndPreviousButton } from './SaveAndPreviousButton';

interface IssueStage {
  key: string;
  label: string;
  seq: number;
  statuses: string[];
}

interface IssueStageConfig {
  stageList: IssueStage[];
}

interface StatusMapFormProps {
  integrationId: string;
  isManager?: boolean;
}

const initialValues: IssueStageConfig = {
  stageList: [],
};

const StatusMapForm = ({
  integrationId,
  isManager = false,
}: StatusMapFormProps) => {
  const [newToast] = useToastHook();
  const queryClient = useQueryClient();
  const [initialIssueValues, setInitialIssueValues] =
    useState<IssueStageConfig>(initialValues);
  const { data: issueStatuses, isLoading: issueStatusLoading } =
    useIssueStatuses(integrationId);

  const {
    mutate: handleSubmitIssueStatus,
    isLoading: handleSubmitIssueStatusLoading,
  } = useConfiguredIssueStatusMutation();

  const {
    data: issueStatusConfigured,
    isLoading: issueStatusConfiguredLoading,
  } = useConfiguredIssueStatus(integrationId);

  useEffect(() => {
    if (!issueStatusConfigured?.dto || !issueStatusConfigured?.dto?.stageList)
      return;
    setInitialIssueValues(issueStatusConfigured?.dto);
  }, [issueStatusConfigured?.dto]);

  const onSubmit = (values: IssueStageConfig) => {
    handleSubmitIssueStatus(
      { ...values, integrationId },
      {
        onSuccess: () => {
          newToast({
            message: 'Stage Mapping done successfully!',
            status: 'success',
          });
          queryClient.invalidateQueries([QUERY_KEYS.stageConfig]);
        },
        onError: (err: any) => {
          newToast({
            message: err?.message ?? `Something went wrong!`,
            status: 'error',
          });
        },
      }
    );
  };

  return (
    <>
      <Text
        fontSize="sm"
        fontFamily="heading"
        fontWeight="semibold"
        color="text.primary"
        lineHeight="17px"
        mb={4}
      >
        Map issue statuses to workflow stages so metrics reflect how work moves
        through your pipeline.
      </Text>
      <Formik
        enableReinitialize
        initialValues={initialIssueValues}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue }: any) => (
          <Form>
            <Stack spacing={4}>
              {(values.stageList || []).map(
                (stage: IssueStage, index: number) => (
                  <HStack spacing={6} key={index}>
                    <Text
                      fontSize="sm"
                      fontFamily="heading"
                      fontWeight="semibold"
                      color="text.secondary"
                      lineHeight="17px"
                      width="150px"
                    >
                      {stage.label}:
                    </Text>
                    <Field
                      as={Select}
                      name={`stageList.${index}.statuses`}
                      placeholder="Select Status"
                      isMulti
                      useBasicStyles
                      closeMenuOnSelect={false}
                      menuPlacement="auto"
                      onChange={(selectedOption: any) => {
                        setFieldValue(
                          `stageList.${index}.statuses`,
                          selectedOption.map((item: any) => item.value)
                        );
                      }}
                      options={issueStatuses?.dto?.map((item: any) => ({
                        value: item,
                        label: item,
                      }))}
                      value={stage.statuses?.map((item: any) => ({
                        value: item,
                        label: item,
                      }))}
                      isDisabled={isManager}
                    />
                  </HStack>
                )
              )}
              <Box mt={6}>
                <SaveAndPreviousButton
                  onSubmit={() => onSubmit(values)}
                  isDisabled={isManager}
                />
              </Box>
            </Stack>
          </Form>
        )}
      </Formik>
      {(issueStatusLoading ||
        handleSubmitIssueStatusLoading ||
        issueStatusConfiguredLoading) && <Loader />}
    </>
  );
};

export default StatusMapForm;
